import { useCallback, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerSummary, DuplicateCheckResult } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { apiErrorMessage } from '@/services/apiClient';
import { useCheckDuplicatesMutation, useCreateCustomerMutation } from '../api/customers.api';
import { hasErrors, toCustomerPayload, validateCustomerForm } from '../validation';
import { CustomerFormFields } from './CustomerFormFields';
import { EMPTY_CUSTOMER_FORM, type CustomerFormErrors, type CustomerFormValues } from '../form';
import { DuplicateCustomerWarning } from './DuplicateCustomerWarning';

interface QuickCreateCustomerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (customer: CustomerSummary) => void;
  /** Selecting an existing record instead of creating one. Used by the POS journey later. */
  onUseExisting?: (customerId: string) => void;
  /** Pre-fills the mobile when opened from a search that found nothing. */
  initialMobile?: string;
}

/**
 * Registration in the time a customer stands at the counter.
 *
 * Only a mobile and a name are required. Duplicates are probed when the mobile field is left, so
 * the warning is already on screen by the time the form is submitted rather than interrupting
 * afterwards.
 */
export function QuickCreateCustomer({
  isOpen,
  onClose,
  onCreated,
  onUseExisting,
  initialMobile = '',
}: QuickCreateCustomerProps) {
  const { t } = useTranslation();

  const [values, setValues] = useState<CustomerFormValues>({
    ...EMPTY_CUSTOMER_FORM,
    mobile: initialMobile,
  });
  const [errors, setErrors] = useState<CustomerFormErrors>({});
  const [duplicates, setDuplicates] = useState<DuplicateCheckResult | null>(null);
  const [acknowledgedDuplicates, setAcknowledgedDuplicates] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createCustomer = useCreateCustomerMutation();
  const checkDuplicates = useCheckDuplicatesMutation();

  const reset = useCallback(() => {
    setValues({ ...EMPTY_CUSTOMER_FORM, mobile: '' });
    setErrors({});
    setDuplicates(null);
    setAcknowledgedDuplicates(false);
    setSubmitError(null);
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const change = useCallback(
    <K extends keyof CustomerFormValues>(field: K, value: CustomerFormValues[K]) => {
      setValues((current) => ({ ...current, [field]: value }));
      setErrors((current) => ({ ...current, [field]: undefined }));
      // A changed mobile invalidates the previous duplicate answer.
      if (field === 'mobile') {
        setDuplicates(null);
        setAcknowledgedDuplicates(false);
      }
    },
    [],
  );

  const probeDuplicates = useCallback(() => {
    const mobile = values.mobile.trim();
    if (mobile.replace(/\D/g, '').length < 6) return;

    checkDuplicates.mutate(
      { mobile, ...(values.email.trim() ? { email: values.email.trim() } : {}) },
      { onSuccess: (result) => setDuplicates(result.hasPossibleDuplicates ? result : null) },
    );
  }, [checkDuplicates, values.email, values.mobile]);

  const submit = useCallback(() => {
    setSubmitError(null);

    createCustomer.mutate(toCustomerPayload(values), {
      onSuccess: (customer) => {
        reset();
        onCreated(customer);
      },
      onError: (error) => setSubmitError(apiErrorMessage(error) ?? t('customers.createError')),
    });
  }, [createCustomer, onCreated, reset, t, values]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validation = validateCustomerForm(values, t);
    setErrors(validation);
    if (hasErrors(validation)) return;

    // Nobody creates a duplicate without having seen the existing record first.
    if (duplicates && !acknowledgedDuplicates) return;

    submit();
  };

  const showDuplicates = Boolean(duplicates && !acknowledgedDuplicates);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('customers.quickCreate.title')}
      description={t('customers.quickCreate.subtitle')}
      footer={
        showDuplicates ? undefined : (
          <>
            <Button variant="secondary" onClick={close} disabled={createCustomer.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="quick-create-customer" isLoading={createCustomer.isPending}>
              {t('customers.quickCreate.submit')}
            </Button>
          </>
        )
      }
    >
      {showDuplicates && duplicates ? (
        <DuplicateCustomerWarning
          result={duplicates}
          isCreating={createCustomer.isPending}
          onUseExisting={(customerId) => {
            reset();
            onUseExisting?.(customerId);
            onClose();
          }}
          onCreateAnyway={() => {
            setAcknowledgedDuplicates(true);
            const validation = validateCustomerForm(values, t);
            setErrors(validation);
            if (!hasErrors(validation)) submit();
          }}
        />
      ) : (
        <form id="quick-create-customer" onSubmit={handleSubmit} noValidate className="space-y-4">
          {submitError ? <Alert tone="error">{submitError}</Alert> : null}

          <CustomerFormFields
            values={values}
            errors={errors}
            onChange={change}
            variant="quick"
            onMobileBlur={probeDuplicates}
          />
        </form>
      )}
    </Modal>
  );
}
